import { z } from 'zod'
import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import {
  ghlBackfillRunDtoSchema,
  ghlBackfillRunListDtoSchema,
  ghlReviewListDtoSchema,
  type GhlBackfillRunDto,
  type GhlBackfillRunListDto,
  type GhlReviewListDto,
} from '@lib/dto/gohighlevel.dto'
import type { ResolveGhlReviewInput } from '@lib/input/gohighlevel.input'
import type { IGhlBackfillService } from '@/types/GhlBackfillService.types'

const base = (venueId: string): string => `/venues/${encodeURIComponent(venueId)}/gohighlevel`

/** A venue's GoHighLevel backfill and review list (docs/gohighlevel.spec.md). */
class GhlBackfillServiceImpl implements IGhlBackfillService {
  async start(venueId: string): Promise<GhlBackfillRunDto> {
    try {
      return await api.post(`${base(venueId)}/backfill`, {}, ghlBackfillRunDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to start a GoHighLevel backfill', { venueId }, error)
      throw error
    }
  }

  async listRuns(venueId: string): Promise<GhlBackfillRunListDto> {
    try {
      return await api.get(`${base(venueId)}/backfill/runs`, ghlBackfillRunListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load GoHighLevel backfills', { venueId }, error)
      throw error
    }
  }

  async getRun(venueId: string, runId: string): Promise<GhlBackfillRunDto> {
    try {
      return await api.get(`${base(venueId)}/backfill/runs/${encodeURIComponent(runId)}`, ghlBackfillRunDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load a GoHighLevel backfill', { venueId, runId }, error)
      throw error
    }
  }

  async review(venueId: string): Promise<GhlReviewListDto> {
    try {
      return await api.get(`${base(venueId)}/review`, ghlReviewListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the GoHighLevel review list', { venueId }, error)
      throw error
    }
  }

  async confirm(venueId: string, itemId: string, input: ResolveGhlReviewInput): Promise<void> {
    try {
      await api.post(`${base(venueId)}/review/${encodeURIComponent(itemId)}/confirm`, input, z.null())
    } catch (error) {
      logRequestFailure('Failed to confirm a GoHighLevel match', { venueId, itemId }, error)
      throw error
    }
  }

  async reject(venueId: string, itemId: string): Promise<void> {
    try {
      await api.post(`${base(venueId)}/review/${encodeURIComponent(itemId)}/reject`, {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to reject a GoHighLevel match', { venueId, itemId }, error)
      throw error
    }
  }
}

export const ghlBackfillService: IGhlBackfillService = new GhlBackfillServiceImpl()
